import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Bot, X, Send, Loader2, Paperclip, RotateCcw, FileText,
  ListTodo, AlertTriangle, CheckSquare, Sparkles, Pencil,
  BellRing, ChevronDown, ChevronRight, Mail, ExternalLink, Scale,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import Markdown from "@/components/Markdown";

const TYPE_META = {
  task: { label: "Task", icon: ListTodo, cls: "bg-blue-100 text-blue-700" },
  risk: { label: "Risk", icon: AlertTriangle, cls: "bg-red-100 text-red-700" },
  action_item: { label: "Action", icon: CheckSquare, cls: "bg-amber-100 text-amber-700" },
  decision: { label: "Decision", icon: Scale, cls: "bg-violet-100 text-violet-700" },
  document: { label: "Document", icon: FileText, cls: "bg-sky-100 text-sky-700" },
  update_task: { label: "Update task", icon: Pencil, cls: "bg-violet-100 text-violet-700" },
  update_risk: { label: "Update risk", icon: Pencil, cls: "bg-violet-100 text-violet-700" },
  update_action_item: { label: "Update action", icon: Pencil, cls: "bg-violet-100 text-violet-700" },
};

const fmtVal = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

function ProposalCard({ proposals, msgIdx, projectId, onCreated }) {
  const [checked, setChecked] = useState(() => proposals.map(() => true));
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const count = checked.filter(Boolean).length;

  const create = async () => {
    if (!projectId) { toast.error("Pick a project at the top of the chat first"); return; }
    setBusy(true);
    try {
      const items = proposals.filter((_, i) => checked[i]);
      const { data } = await api.post("/helper/execute", { project_id: projectId, items });
      setDone(true);
      toast.success(data.summary);
      onCreated(data.summary, projectId);
    } catch (e) {
      toast.error(formatApiError(e, "Could not create items"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 border border-border rounded-xl bg-card overflow-hidden" data-testid={`pm-helper-proposals-${msgIdx}`}>
      <div className="px-3 py-2 bg-secondary/60 text-xs font-semibold flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-primary" /> Drafted items — review &amp; confirm
      </div>
      <div className="max-h-64 overflow-y-auto divide-y divide-border">
        {proposals.map((p, i) => {
          const meta = TYPE_META[p.type] || TYPE_META.task;
          const Icon = meta.icon;
          return (
            <label key={i} className="flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-secondary/40" data-testid={`pm-helper-proposal-${msgIdx}-${i}`}>
              <Checkbox
                checked={checked[i]}
                disabled={done}
                onCheckedChange={(v) => setChecked((c) => c.map((x, j) => (j === i ? !!v : x)))}
                className="mt-0.5"
                data-testid={`pm-helper-proposal-check-${msgIdx}-${i}`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${meta.cls}`}>
                    <Icon className="h-3 w-3" /> {p.is_milestone ? "Milestone" : meta.label}
                  </span>
                  <span className="text-sm font-medium truncate">{p.title}</span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {p.type === "task" && [p.phase, p.start_date && `${p.start_date} → ${p.end_date || "?"}`, p.assignee].filter(Boolean).join(" · ")}
                  {p.type === "risk" && `P${p.probability || 3}×I${p.impact || 3}${p.owner ? ` · ${p.owner}` : ""}`}
                  {p.type === "action_item" && [p.owner, p.due_date && `due ${p.due_date}`].filter(Boolean).join(" · ")}
                  {p.type === "decision" && [p.owner && `decider: ${p.owner}`, p.due_date && `needed by ${p.due_date}`, p.blocking && "BLOCKING"].filter(Boolean).join(" · ")}
                  {p.type === "document" && (
                    <span>
                      {(p.category || "Report")} PDF → saved to Library ▸ Artifacts
                      <span className="block mt-0.5 opacity-75 line-clamp-2">{(p.content || "").slice(0, 140)}…</span>
                    </span>
                  )}
                  {p.type?.startsWith("update_") && p.changes && (
                    <span className="space-x-2">
                      {Object.entries(p.changes).map(([k, v]) => (
                        <span key={k}>
                          {k.replace(/_/g, " ")}: <span className="line-through opacity-60">{fmtVal(p.current?.[k])}</span> → <span className="font-semibold text-violet-700">{fmtVal(v)}</span>
                        </span>
                      ))}
                    </span>
                  )}
                </div>
              </div>
            </label>
          );
        })}
      </div>
      <div className="p-2 border-t border-border">
        {done ? (
          <div className="text-xs text-emerald-600 font-medium px-1" data-testid={`pm-helper-created-${msgIdx}`}>✓ Applied</div>
        ) : (
          <Button size="sm" className="w-full" onClick={create} disabled={busy || !count} data-testid={`pm-helper-create-btn-${msgIdx}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Confirm ${count} selected item${count === 1 ? "" : "s"}`}
          </Button>
        )}
      </div>
    </div>
  );
}

function NudgeBox({ person, onClose, onSent }) {
  const [draft, setDraft] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.post("/helper/nudge-draft", { person })
      .then(({ data }) => { setDraft(data.draft); setEmail(data.email || ""); })
      .catch((e) => { toast.error(formatApiError(e, "Could not draft the note")); onClose(); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person]);

  const send = async () => {
    if (!email.trim()) { toast.error("Add an email address first"); return; }
    setSending(true);
    try {
      await api.post("/helper/nudge-send", { person, message: draft, email: email.trim() });
      toast.success(`Nudge sent to ${person}`);
      onSent();
    } catch (e) {
      toast.error(formatApiError(e, "Could not send"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-1 mb-2 bg-white border border-violet-200 rounded-lg p-2 space-y-1.5" data-testid={`pm-helper-nudge-box-${person}`}>
      {loading ? (
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground py-1"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Drafting a check-in note for {person}…</div>
      ) : (
        <>
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={4}
                    className="w-full text-[12px] rounded-md border border-input p-2 resize-none focus:outline-none focus:ring-1 focus:ring-ring"
                    data-testid={`pm-helper-nudge-draft-${person}`} />
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="their@email.com"
                 className="w-full text-[11px] rounded-md border border-input px-2 py-1 focus:outline-none focus:ring-1 focus:ring-ring"
                 data-testid={`pm-helper-nudge-email-${person}`} />
          <div className="flex gap-1.5">
            <Button size="sm" className="h-7 text-[11px] flex-1" onClick={send} disabled={sending || !draft.trim()} data-testid={`pm-helper-nudge-send-${person}`}>
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3 w-3 mr-1" /> Send with their open items</>}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={onClose}>Cancel</Button>
          </div>
        </>
      )}
    </div>
  );
}

function BriefingCard({ briefing, digestOn, onDigestToggle, onNavigate, onNudgeSent, onDraftRisks }) {
  const [open, setOpen] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [nudging, setNudging] = useState(null);
  const [nudged, setNudged] = useState({});
  if (!briefing) return null;
  const pos = (briefing.plan_position || []).filter((p) => p.gap > 0 || p.slipped_vs_baseline > 0 || p.predicted_late > 0);
  if (!briefing.count && !pos.length) return null;
  return (
    <div className="border border-amber-200 bg-amber-50/60 rounded-xl overflow-hidden" data-testid="pm-helper-briefing">
      <button className="w-full px-3 py-2 flex items-center gap-2 text-left" onClick={() => setOpen((o) => !o)} data-testid="pm-helper-briefing-toggle">
        <BellRing className="h-4 w-4 text-amber-600 shrink-0" />
        <span className="text-xs font-bold flex-1">Your briefing — {briefing.count} item{briefing.count === 1 ? "" : "s"} need attention</span>
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="px-3 pb-2 space-y-2">
          {pos.length > 0 && (
            <div className="bg-white/70 rounded-lg p-2 space-y-1" data-testid="pm-helper-plan-position">
              <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Plan position (actual vs expected)</div>
              {pos.slice(0, 5).map((p) => (
                <button key={p.project_id} className="w-full text-left text-[11px] flex items-center gap-1.5 hover:underline"
                        onClick={() => onNavigate(`/projects/${p.project_id}?tab=plan`)}>
                  <span className="font-semibold truncate">{p.project_name}</span>
                  <span className={p.gap >= 10 ? "text-red-600 font-bold" : p.gap > 0 ? "text-amber-700 font-bold" : "text-emerald-600"}>
                    {p.actual_pct}% vs {p.expected_pct}%{p.gap > 0 ? ` (−${p.gap} pts)` : " ✓"}
                  </span>
                  {p.slipped_vs_baseline > 0 && <span className="text-muted-foreground">· {p.slipped_vs_baseline} slipped vs baseline</span>}
                  {p.predicted_late > 0 && <span className="text-red-600">· {p.predicted_late} forecast late</span>}
                </button>
              ))}
            </div>
          )}
          {(briefing.sections || []).map((sec) => {
            const show = expanded[sec.key] ? sec.items : sec.items.slice(0, 3);
            const nudgeable = sec.key === "behind_resources";
            return (
              <div key={sec.key} data-testid={`pm-helper-brief-${sec.key}`}>
                <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{sec.title} ({sec.items.length})</div>
                {show.map((it, i) => (
                  <div key={i}>
                    <div className="flex items-start gap-1 py-0.5">
                      <button className="flex-1 text-left text-[11px] flex items-start gap-1 hover:underline min-w-0"
                              onClick={() => onNavigate(`/projects/${it.project_id}?tab=${sec.key === "trueups" ? "dashboard" : sec.key === "audit" ? "audit" : "plan"}`)}>
                        <ExternalLink className="h-3 w-3 mt-0.5 shrink-0 text-muted-foreground" />
                        <span><span className="font-semibold">{it.label}</span> <span className="text-muted-foreground">— {it.detail} · {it.project_name}</span></span>
                      </button>
                      {nudgeable && (
                        nudged[it.label] ? (
                          <span className="text-[10px] text-emerald-600 font-semibold shrink-0" data-testid={`pm-helper-nudged-${it.label}`}>Sent ✓</span>
                        ) : (
                          <button
                            className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 hover:bg-violet-200 shrink-0"
                            onClick={() => setNudging(nudging === it.label ? null : it.label)}
                            data-testid={`pm-helper-nudge-${it.label}`}
                          >
                            Nudge
                          </button>
                        )
                      )}
                    </div>
                    {nudgeable && nudging === it.label && (
                      <NudgeBox person={it.label} onClose={() => setNudging(null)}
                                onSent={() => { setNudging(null); setNudged((n) => ({ ...n, [it.label]: true })); onNudgeSent(it.label); }} />
                    )}
                  </div>
                ))}
                {sec.items.length > 3 && !expanded[sec.key] && (
                  <button className="text-[11px] text-primary font-semibold" onClick={() => setExpanded((e) => ({ ...e, [sec.key]: true }))}>
                    +{sec.items.length - 3} more…
                  </button>
                )}
                {sec.key === "needs_risk" && (
                  <button className="mt-1 text-[11px] font-bold px-2 py-1 rounded bg-violet-100 text-violet-700 hover:bg-violet-200 inline-flex items-center gap-1"
                          onClick={onDraftRisks} data-testid="pm-helper-draft-risks">
                    <Sparkles className="h-3 w-3" /> Draft these risks with AI
                  </button>
                )}
              </div>
            );
          })}
          <div className="flex items-center gap-2 pt-1 border-t border-amber-200/70">
            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-[11px] text-muted-foreground flex-1">Email me this briefing daily</span>
            <Switch checked={digestOn} onCheckedChange={onDigestToggle} className="scale-75" data-testid="pm-helper-digest-toggle" />
          </div>
        </div>
      )}
    </div>
  );
}

export default function PMAssistant() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("all");
  const [input, setInput] = useState("");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [digestOn, setDigestOn] = useState(true);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  const routeProject = useMemo(() => {
    const m = location.pathname.match(/^\/projects\/([\w-]+)/);
    return m ? m[1] : null;
  }, [location.pathname]);

  useEffect(() => {
    if (routeProject) setProjectId(routeProject);
  }, [routeProject]);

  useEffect(() => {
    if (!user) return;
    api.get("/helper/briefing").then(({ data }) => setBriefing(data)).catch(() => {});
    api.get("/helper/digest-settings").then(({ data }) => setDigestOn(data.enabled)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!open || loaded || !user) return;
    setLoaded(true);
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
    api.get("/helper/history").then(({ data }) => setMessages(data)).catch(() => {});
  }, [open, loaded, user]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, open]);

  const hidden =
    !user ||
    /^\/(login|register|accept-invite|my-card|shared)/.test(location.pathname);
  if (hidden) return null;

  const pid = projectId === "all" ? null : projectId;

  const send = async (textOverride) => {
    const text = (typeof textOverride === "string" ? textOverride : input).trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    const docIds = docs.map((d) => d.id);
    setDocs([]);
    try {
      const { data } = await api.post("/helper/chat", { message: text, project_id: pid, doc_ids: docIds });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, proposals: data.proposals }]);
    } catch (e) {
      toast.error(formatApiError(e, "Project Maestro error"));
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — something went wrong. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const upload = async (files) => {
    for (const f of Array.from(files || [])) {
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/helper/upload", fd, { timeout: 90000 });
        setDocs((d) => [...d, data]);
        toast.success(`${data.filename} attached`);
      } catch (e) {
        toast.error(formatApiError(e, `Could not read ${f.name}`));
      } finally {
        setUploading(false);
      }
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const clearChat = async () => {
    try {
      await api.post("/helper/clear");
      setMessages([]);
      setDocs([]);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onCreated = (summary, createdPid) => {
    setMessages((m) => [...m, { role: "assistant", content: `✅ ${summary}` }]);
    api.get("/helper/briefing").then(({ data }) => setBriefing(data)).catch(() => {});
    window.dispatchEvent(new CustomEvent("orbit-data-changed", { detail: { projectId: createdPid } }));
  };

  const toggleDigest = async (v) => {
    setDigestOn(v);
    try {
      await api.put("/helper/digest-settings", { enabled: v });
      toast.success(v ? "Daily briefing email on" : "Daily briefing email off");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <>
      {!open && (
        <div className="fixed top-16 right-5 z-50 group">
          <button
            onClick={() => setOpen(true)}
            className="relative h-11 pl-3.5 pr-4 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg shadow-orange-500/30 flex items-center gap-2 hover:brightness-110 hover:-translate-y-0.5 transition-all font-bold text-sm"
            data-testid="pm-helper-fab"
          >
            <Bot className="h-5 w-5" /> Project Maestro
            {briefing?.count > 0 && (
              <span className="absolute -top-1.5 -right-1 min-w-[22px] h-[22px] px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center border-2 border-background"
                    data-testid="pm-helper-badge">
                {briefing.count > 99 ? "99+" : briefing.count}
              </span>
            )}
          </button>
          <div className="absolute right-0 top-full mt-2 w-80 rounded-xl bg-slate-900 text-slate-100 text-xs p-3.5 shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none z-50"
               data-testid="pm-helper-tooltip">
            <p className="font-bold text-sm mb-1.5 flex items-center gap-1.5"><Bot className="h-4 w-4 text-amber-400" /> Project Maestro — your Project Management Orchestrator</p>
            <ul className="space-y-1 leading-relaxed">
              <li>• Ask anything about your projects — "what's overdue?", "who's overloaded?"</li>
              <li>• Upload a charter, SOW or notes and it drafts tasks, risks &amp; action items for your approval</li>
              <li>• Update the plan by chat — "push UAT out a week", "mark the audit done"</li>
              <li>• Creates documents (reports, summaries) as PDFs in your Artifacts library</li>
              <li>• Daily briefing: behind-plan work, true-ups due, audit gaps — with one-click nudge emails</li>
            </ul>
          </div>
        </div>
      )}

      {open && (
        <div
          className="fixed top-16 right-4 z-50 w-[min(430px,calc(100vw-2rem))] h-[min(640px,calc(100vh-5.5rem))] bg-background border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="pm-helper-panel"
        >
          <div className="px-4 py-3 border-b border-border bg-secondary/50 flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary shrink-0" />
            <div className="font-display font-bold text-sm shrink-0" title="Project Management Orchestrator">Project Maestro</div>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="h-8 text-xs flex-1 min-w-0 bg-background" data-testid="pm-helper-project-select">
                <SelectValue placeholder="Project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All projects (portfolio)</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={clearChat} title="New chat" data-testid="pm-helper-clear">
              <RotateCcw className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setOpen(false)} data-testid="pm-helper-close">
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            <BriefingCard briefing={briefing} digestOn={digestOn} onDigestToggle={toggleDigest}
                          onNavigate={(to) => { navigate(to); }}
                          onDraftRisks={() => send("Draft a risk for each task in my briefing that needs one, and link each risk to its task using for_task_id.")}
                          onNudgeSent={(person) => setMessages((m) => [...m, { role: "assistant", content: `📨 Nudge sent to ${person}.` }])} />
            {messages.length === 0 && (
              <div className="text-sm text-muted-foreground bg-secondary/50 rounded-xl p-4 space-y-2" data-testid="pm-helper-greeting">
                <p className="font-semibold text-foreground flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-primary" /> Hi{user?.name ? ` ${user.name.split(" ")[0]}` : ""}! I'm Project Maestro — your Project Management Orchestrator.</p>
                <p>I can answer questions about your projects — try <em>"what's overdue?"</em> or <em>"who has the most open work?"</em></p>
                <p>Attach a document (charter, SOW, meeting notes…) and ask me to <em>draft tasks, risks or action items</em> from it — or ask me to <em>update existing items</em> ("push UAT out a week"). You'll review everything before it's applied.</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[88%]">
                  <div
                    className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-primary text-primary-foreground rounded-br-sm whitespace-pre-wrap"
                        : "bg-secondary text-foreground rounded-bl-sm"
                    }`}
                    data-testid={`pm-helper-msg-${m.role}`}
                  >
                    {m.role === "assistant" ? <Markdown text={m.content} /> : m.content}
                  </div>
                  {m.role === "assistant" && m.proposals?.length > 0 && (
                    <ProposalCard proposals={m.proposals} msgIdx={i} projectId={pid || m.project_id} onCreated={onCreated} />
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-secondary rounded-2xl rounded-bl-sm px-3.5 py-2.5">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="border-t border-border p-3 space-y-2">
            {docs.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {docs.map((d) => (
                  <span key={d.id} className="inline-flex items-center gap-1 text-[11px] bg-secondary rounded-full pl-2 pr-1 py-0.5" data-testid={`pm-helper-doc-${d.id}`}>
                    <FileText className="h-3 w-3 text-primary" /> {d.filename}
                    <button onClick={() => setDocs((x) => x.filter((y) => y.id !== d.id))} className="hover:bg-border rounded-full p-0.5">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <input ref={fileRef} type="file" multiple className="hidden" data-testid="pm-helper-file-input"
                     accept=".docx,.pdf,.pptx,.xlsx,.csv,.txt,.md" onChange={(e) => upload(e.target.files)} />
              <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" disabled={uploading}
                      onClick={() => fileRef.current?.click()} title="Attach a document" data-testid="pm-helper-upload">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
              </Button>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={pid ? "Ask about this project or draft items…" : "Ask about your portfolio…"}
                rows={1}
                className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring max-h-28"
                data-testid="pm-helper-input"
              />
              <Button size="icon" className="h-10 w-10 shrink-0" onClick={send} disabled={loading || !input.trim()} data-testid="pm-helper-send">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
